import twilio from 'twilio';
import type { Twilio } from 'twilio';
import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/**
 * Twilio messaging integration
 */
export class TwilioIntegration extends BaseIntegration {
  private client: Twilio;
  private phoneNumber: string;

  constructor(config: IntegrationConfig) {
    super(config);

    const accountSid = config.env.TWILIO_ACCOUNT_SID;
    const authToken = config.env.TWILIO_AUTH_TOKEN;
    this.phoneNumber = config.env.TWILIO_PHONE_NUMBER || '';

    if (!accountSid || !authToken) {
      throw new Error('TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN not configured');
    }

    this.client = twilio(accountSid, authToken);

    this.logger.info('Twilio integration initialized');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    // Validate params against registry
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();
    let retries = 0;

    try {
      let data: unknown;

      switch (action) {
        case 'sendSMS':
          data = await this.executeWithRetry(() => this.sendSMS(params));
          break;
        case 'sendWhatsApp':
          data = await this.executeWithRetry(() => this.sendWhatsApp(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime, retries),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async sendSMS(params: IntegrationParams) {
    const { to, body } = params;

    this.logger.debug('Sending SMS', { to: String(to ?? '') });

    const message = await this.client.messages.create({
      from: this.phoneNumber,
      to: to as string,
      body: body as string,
    });

    return {
      sid: message.sid,
      status: message.status,
    };
  }

  private async sendWhatsApp(params: IntegrationParams) {
    const { to, body } = params;

    this.logger.debug('Sending WhatsApp message', { to: String(to ?? '') });

    const message = await this.client.messages.create({
      from: `whatsapp:${this.phoneNumber}`,
      to: `whatsapp:${to}`,
      body: body as string,
    });

    return {
      sid: message.sid,
      status: message.status,
    };
  }
}

// Auto-register
registerIntegration('twilio', TwilioIntegration);
